import * as i0 from '@angular/core';
import { forwardRef, Component, ChangeDetectionStrategy, ViewEncapsulation, ViewChild, Input, NgModule } from '@angular/core';
import * as i2 from 'angular-imask';
import { IMaskDirective, IMaskModule } from 'angular-imask';
import { createMask } from 'imask';
import { MXInputBaseValueComponent, MXLookComponent, AXComponent, AXFocusableComponent, AXClearableComponent, AXValuableComponent } from '@acorex/components/common';
import * as i1 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { toNumber, isEmpty } from 'lodash-es';
import { classes } from 'polytype';
import { AXButtonModule } from '@acorex/components/button';
import { AXDecoratorModule } from '@acorex/components/decorators';
import { CommonModule } from '@angular/common';

/**
 * The NumberBox is a component which detects user interaction and triggers a corresponding event
 *
 * @category Components
 */
class AXNumberBoxComponent extends classes((MXInputBaseValueComponent), MXLookComponent) {
    constructor() {
        super(...arguments);
        this._padDecimalZeros = false;
        /**
         *  Specifies the value that is used to increment or decrement the **Integer** part of number
         */
        this.step = 1;
        this._decimals = 0;
        /**
         *  Specifies the value that is used to increment or decrement the **Deciaml** part of number
         */
        this.decimalStep = 'auto';
        /**
         *  Specifies whether the Up and Down spin buttons will be rendered or not
         */
        this.showSpinButtons = true;
        this._minValue = Number.MIN_SAFE_INTEGER;
        this._maxValue = Number.MAX_SAFE_INTEGER;
    }
    /**
     *  A character value that specifies the separator character.
     */
    get thousandsSeparator() {
        return this._thousandsSeparator;
    }
    set thousandsSeparator(v) {
        this.setOption({
            name: 'thousandsSeparator',
            value: v ?? '',
        });
    }
    /**
     *  A character value that specifies the separator character.
     */
    get padDecimalZeros() {
        return this._padDecimalZeros;
    }
    set padDecimalZeros(v) {
        this.setOption({
            name: 'padDecimalZeros',
            value: v,
        });
    }
    /**
     *  Specifies the number of decimals that the user can enter
     */
    get decimals() {
        return this._decimals;
    }
    set decimals(v) {
        this.setOption({
            name: 'decimals',
            value: v ?? 0,
        });
    }
    /**
     *  Specifies the smallest value that is valid
     */
    get minValue() {
        return this._minValue;
    }
    set minValue(v) {
        this.setOption({
            name: 'minValue',
            value: v != null ? Number(v) : Number.MIN_SAFE_INTEGER,
        });
    }
    /**
     *  Specifies the greatest value that is valid
     */
    get maxValue() {
        return this._maxValue;
    }
    set maxValue(v) {
        this.setOption({
            name: 'maxValue',
            value: v != null ? Number(v) : Number.MAX_SAFE_INTEGER,
        });
    }
    ngAfterViewInit() {
        this._updateMask();
    }
    internalOptionChanged() {
        this._updateMask();
    }
    reset() {
        this.input.nativeElement.value = null;
        super.reset(true);
    }
    internalSetValue(value) {
        // || (value == 0 && !this._maskObj.value)
        if (value == null) {
            return this.defaultValue;
        }
        const v = toNumber(value);
        if (v < this.minValue)
            return this.minValue;
        if (v > this.maxValue)
            return this.maxValue;
        return v;
    }
    _updateMask() {
        if (!this._maskObj) {
            this._maskObj = createMask({
                mask: Number,
                min: this.minValue,
                max: this.maxValue,
                thousandsSeparator: this.thousandsSeparator || '',
                scale: this.decimals,
                radix: '.',
                padFractionalZeros: this.decimals > 0 && this.padDecimalZeros,
            });
            this._maskObj.format = (v, m) => {
                return !isNaN(v)
                    ? v.toLocaleString('en-US', {
                        useGrouping: false,
                        maximumFractionDigits: 20,
                    })
                    : '';
            };
        }
        else {
            this._maskObj.updateOptions({
                min: this.minValue,
                max: this.maxValue,
                thousandsSeparator: this.thousandsSeparator || '',
                scale: this.decimals,
                radix: '.',
                padFractionalZeros: this.decimals > 0 && this.padDecimalZeros,
            });
        }
        this._maskDirective?.maskRef?.updateValue();
        this._maskDirective?.maskRef?.updateControl();
    }
    _handleModelChange(value) {
        if (value == 0 && isEmpty(this.input?.nativeElement?.value)) {
            value = null;
        }
        this.commitValue(value, true);
    }
    _handleOnKeydownEvent(e) {
        if (this.disabled || this.readonly)
            return;
        const ignore = () => {
            e.preventDefault();
            e.stopPropagation();
        };
        const input = e.target;
        const valStr = input.value;
        const decimalPointIndex = valStr.indexOf('.');
        const _cursorPos = input.selectionStart;
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            if (decimalPointIndex > 0 && _cursorPos > decimalPointIndex && this.decimals > 0) {
                e.key === 'ArrowDown' ? this.decimalStepDown(true) : this.decimalStepUp(true);
            }
            else {
                e.key === 'ArrowDown' ? this.stepDown(true) : this.stepUp(true);
            }
            ignore();
            setTimeout(() => {
                input.setSelectionRange(_cursorPos, _cursorPos);
            }, 5);
        }
        super.emitOnKeydownEvent(e);
    }
    _handleUpDownClick(v) {
        if (this.disabled || this.readonly)
            return;
        v == 1 ? this.stepUp(true) : this.stepDown(true);
    }
    stepUp(u = false) {
        if (!this.value) {
            this.commitValue(Math.max(this.minValue, 0), u);
        }
        this.commitValue(this.value + toNumber(this.step), u);
    }
    stepDown(u = false) {
        if (!this.value) {
            this.commitValue(Math.max(this.minValue, 0), u);
        }
        this.commitValue(this.value - toNumber(this.step), u);
    }
    decimalStepUp(u = false) {
        if (!this.value) {
            this.commitValue(Math.max(this.minValue, 0), u);
        }
        this.commitValue(this.value + toNumber(this.decimalStep == 'auto' ? 1 / Math.pow(10, this.decimals) : this.decimalStep), u);
    }
    decimalStepDown(u = false) {
        if (!this.value) {
            this.commitValue(Math.max(this.minValue, 0), u);
        }
        this.commitValue(this.value - toNumber(this.decimalStep == 'auto' ? 1 / Math.pow(10, this.decimals) : this.decimalStep), u);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberBoxComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.0.2", type: AXNumberBoxComponent, selector: "ax-number-box", inputs: { disabled: "disabled", readonly: "readonly", tabIndex: "tabIndex", placeholder: "placeholder", value: "value", state: "state", name: "name", id: "id", look: "look", thousandsSeparator: "thousandsSeparator", padDecimalZeros: "padDecimalZeros", step: "step", decimals: "decimals", decimalStep: "decimalStep", showSpinButtons: "showSpinButtons", minValue: "minValue", maxValue: "maxValue", classNames: ["class", "classNames"] }, outputs: { valueChange: "valueChange", stateChange: "stateChange", onValueChanged: "onValueChanged", onBlur: "onBlur", onFocus: "onFocus", readonlyChange: "readonlyChange", disabledChange: "disabledChange", onKeyDown: "onKeyDown", onKeyUp: "onKeyUp", onKeyPress: "onKeyPress" }, providers: [
            { provide: AXComponent, useExisting: AXNumberBoxComponent },
            { provide: AXFocusableComponent, useExisting: AXNumberBoxComponent },
            { provide: AXClearableComponent, useExisting: AXNumberBoxComponent },
            { provide: AXValuableComponent, useExisting: AXNumberBoxComponent },
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AXNumberBoxComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true }, { propertyName: "_maskDirective", first: true, predicate: IMaskDirective, descendants: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"ax-editor-container ax-look-{{look}} {{classNames}}\" [class.ax-state-disabled]=\"disabled\">\n  <ng-content select=\"ax-prefix\"> </ng-content>\n  <input #input class=\"ax-input\" type=\"text\" [id]=\"id\" [name]=\"name\" [attr.placeholder]=\"placeholder\"\n    [attr.autocomplete]=\"'off'\" [imask]=\"_maskObj\" [unmask]=\"'typed'\" [attr.min]=\"minValue\" [attr.max]=\"maxValue\"\n    [class.ax-state-disabled]=\"disabled\" [class.ax-state-readonly]=\"readonly\" [disabled]=\"disabled\"\n    [readonly]=\"readonly\" [tabindex]=\"tabIndex\" [ngModel]=\"value\" (ngModelChange)=\"_handleModelChange($event)\"\n    (keydown)=\"_handleOnKeydownEvent($event)\" (keyup)=\"emitOnKeyupEvent($event)\"\n    (keypress)=\"emitOnKeypressEvent($event)\" (focus)=\"emitOnFocusEvent($event)\" (blur)=\"emitOnBlurEvent($event)\" />\n  @if(input.value && !disabled && !readonly){\n  <ng-content select=\"ax-clear-button\"></ng-content>\n  }\n  @if(showSpinButtons){\n  <div class=\"ax-number-box-control\">\n    <button type=\"button\" class=\"ax-general-button ax-button-icon\" [class.ax-state-disabled]=\"disabled\"\n      (click)=\"_handleUpDownClick(1)\" tabindex=\"-1\" [disabled]=\"disabled\"><span\n        class=\"ax-icon ax-icon-chevron-up ax-arrow-button \"></span></button>\n    <button type=\"button\" class=\"ax-general-button ax-button-icon\" [class.ax-state-disabled]=\"disabled\"\n      (click)=\"_handleUpDownClick(-1)\" [disabled]=\"disabled\"><span\n        class=\"ax-icon ax-icon-chevron-down ax-arrow-button \"></span></button>\n  </div>\n\n  }\n\n  <ng-content select=\"ax-suffix\"> </ng-content>\n</div>\n<ng-content select=\"ax-validation-rule\"> </ng-content>\n<div class=\"ax-error-container\"></div>", styles: [".ax-number-box-control{display:flex;flex-direction:column}.ax-number-box-control button,.ax-number-box-control button .ax-icon{height:fit-content!important}\n"], dependencies: [{ kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i2.IMaskDirective, selector: "[imask]", inputs: ["imask", "unmask", "imaskElement"], outputs: ["accept", "complete"], exportAs: ["imask"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberBoxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-number-box', inputs: ['disabled', 'readonly', 'tabIndex', 'placeholder', 'value', 'state', 'name', 'id', 'look'], outputs: [
                        'valueChange',
                        'stateChange',
                        'onValueChanged',
                        'onBlur',
                        'onFocus',
                        'readonlyChange',
                        'disabledChange',
                        'onKeyDown',
                        'onKeyUp',
                        'onKeyPress',
                    ], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [
                        { provide: AXComponent, useExisting: AXNumberBoxComponent },
                        { provide: AXFocusableComponent, useExisting: AXNumberBoxComponent },
                        { provide: AXClearableComponent, useExisting: AXNumberBoxComponent },
                        { provide: AXValuableComponent, useExisting: AXNumberBoxComponent },
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AXNumberBoxComponent),
                            multi: true,
                        },
                    ], template: "<div class=\"ax-editor-container ax-look-{{look}} {{classNames}}\" [class.ax-state-disabled]=\"disabled\">\n  <ng-content select=\"ax-prefix\"> </ng-content>\n  <input #input class=\"ax-input\" type=\"text\" [id]=\"id\" [name]=\"name\" [attr.placeholder]=\"placeholder\"\n    [attr.autocomplete]=\"'off'\" [imask]=\"_maskObj\" [unmask]=\"'typed'\" [attr.min]=\"minValue\" [attr.max]=\"maxValue\"\n    [class.ax-state-disabled]=\"disabled\" [class.ax-state-readonly]=\"readonly\" [disabled]=\"disabled\"\n    [readonly]=\"readonly\" [tabindex]=\"tabIndex\" [ngModel]=\"value\" (ngModelChange)=\"_handleModelChange($event)\"\n    (keydown)=\"_handleOnKeydownEvent($event)\" (keyup)=\"emitOnKeyupEvent($event)\"\n    (keypress)=\"emitOnKeypressEvent($event)\" (focus)=\"emitOnFocusEvent($event)\" (blur)=\"emitOnBlurEvent($event)\" />\n  @if(input.value && !disabled && !readonly){\n  <ng-content select=\"ax-clear-button\"></ng-content>\n  }\n  @if(showSpinButtons){\n  <div class=\"ax-number-box-control\">\n    <button type=\"button\" class=\"ax-general-button ax-button-icon\" [class.ax-state-disabled]=\"disabled\"\n      (click)=\"_handleUpDownClick(1)\" tabindex=\"-1\" [disabled]=\"disabled\"><span\n        class=\"ax-icon ax-icon-chevron-up ax-arrow-button \"></span></button>\n    <button type=\"button\" class=\"ax-general-button ax-button-icon\" [class.ax-state-disabled]=\"disabled\"\n      (click)=\"_handleUpDownClick(-1)\" [disabled]=\"disabled\"><span\n        class=\"ax-icon ax-icon-chevron-down ax-arrow-button \"></span></button>\n  </div>\n\n  }\n\n  <ng-content select=\"ax-suffix\"> </ng-content>\n</div>\n<ng-content select=\"ax-validation-rule\"> </ng-content>\n<div class=\"ax-error-container\"></div>", styles: [".ax-number-box-control{display:flex;flex-direction:column}.ax-number-box-control button,.ax-number-box-control button .ax-icon{height:fit-content!important}\n"] }]
        }], propDecorators: { input: [{
                type: ViewChild,
                args: ['input']
            }], thousandsSeparator: [{
                type: Input
            }], padDecimalZeros: [{
                type: Input
            }], step: [{
                type: Input
            }], decimals: [{
                type: Input
            }], decimalStep: [{
                type: Input
            }], showSpinButtons: [{
                type: Input
            }], minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], classNames: [{
                type: Input,
                args: ['class']
            }], _maskDirective: [{
                type: ViewChild,
                args: [IMaskDirective]
            }] } });

const COMPONENT = [AXNumberBoxComponent];
const MODULES = [CommonModule, FormsModule, AXButtonModule, AXDecoratorModule, IMaskModule];
class AXNumberBoxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberBoxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXNumberBoxModule, declarations: [AXNumberBoxComponent], imports: [CommonModule, FormsModule, AXButtonModule, AXDecoratorModule, IMaskModule], exports: [AXNumberBoxComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberBoxModule, imports: [MODULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXNumberBoxModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENT],
                    imports: [...MODULES],
                    exports: [...COMPONENT],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXNumberBoxComponent, AXNumberBoxModule };
